import { Category } from '@flocasts/flosports30-types/dist/entity'
import { CollectionHeaderComponentProps } from '../collection-header/collection-header.interface'
import { FAVORITE_COLLECTION_TYPES } from '../../../../singleton-services/user-preferences/types'
import { environment } from '../../../../../environments/environment'
import { SiteIds } from '../../../../shared/models/site.model'
import { CollectionEntityType } from '../../../../../io/models/collection'

export const collectionFavoritable = (
  isFavoriteVertical: boolean,
  siteId: number | undefined,
  collectionType: CollectionEntityType | 'contentcategory',
  categories: readonly Category[] | undefined,
  coreNodeId: number | undefined
): boolean => {
  if (!coreNodeId) return false
  // Tracks and Series on Floracing
  if (siteId === SiteIds.FLORACING) return isFavoriteCategoryFloracing(categories)
  // Athletes and Teams on Flograppling, Flowrestling, Flohoops, etc.
  return isFavoriteVertical && ['person', 'team'].some(type => type === collectionType)
}
/**
 * For floracing, we need to avoid favoriting top level categories; if the parent
 * of the collection has a categoryID of either of these we are safe
 */
enum ELIGIBLE_FLORACING_PARENT {
  LEVEL = environment.levelCategoryId,
  GOVERNING_BODY = environment.governingBodyCategoryId
}

/**
 * Determine if collection is eligible for floracing favoriting
 */
export const isFavoriteCategoryFloracing = (maybeCategory: readonly Category[] | undefined): boolean => {
  const parentCategory =
    maybeCategory !== undefined && maybeCategory.length > 0 && maybeCategory[0].parent !== undefined
      ? maybeCategory[0].parent
      : undefined
  if (parentCategory && parentCategory.id) {
    // if the parent category is either level or governing body, it is eligible
    return (
      parentCategory.id === ELIGIBLE_FLORACING_PARENT.LEVEL ||
      parentCategory.id === ELIGIBLE_FLORACING_PARENT.GOVERNING_BODY
    )
  }
  return false
}

/**
 * Categories cannot reliably use values in original_entity, so categories[0] must be used
 */
export const getCategoryTrueId = (props: CollectionPropsSubset): number | undefined => {
  if (props.original_entity) {
    const possibleNodeType = props.original_entity.type
    const categoryNodeId = props.categories && props.categories.length > 0 ? props.categories[0].id : undefined
    return FAVORITE_COLLECTION_TYPES.includes(possibleNodeType ?? '') ? props.original_entity?.id : categoryNodeId
  }
  return undefined
}

/**
 * Categories cannot reliably use values in original_entity, so categories[0] must be used. Other types (person, team, etc)
 * will still use original_entity .
 */
export const getCategoryTrueType = (
  props: CollectionPropsSubset
): CollectionEntityType | 'contentcategory' | undefined => {
  if (props.original_entity) {
    const possibleNodeType = props.original_entity.type

    // this checks if its a person, team, or contentcategory, in which case we
    // will use the original_entity type property (not typescript type)
    if (FAVORITE_COLLECTION_TYPES.includes(possibleNodeType ?? '')) return possibleNodeType
  }

  // if collection has an array of categories of length > 0, it will have length === 1 and it will be of type Category.
  // The user-prefs service considers these to have type: "contentcategory", so that is what we will use
  if (props.categories && props.categories.length > 0) return 'contentcategory'
  return undefined
}

/**
 * To determine which property to use, we only need a small subset of the entire CollectionHeaderComponentProps interface
 */
export type CollectionPropsSubset = Pick<CollectionHeaderComponentProps, 'original_entity' | 'categories'>
